<template>
  <AuthLayout center>
    <v-card flat slot="form" class="w-100 transparent box">
      <v-card-text class="mx-wrap">
        <transition name="mx-fade" mode="out-in">
          <!-- Success / already registered -->
          <div v-if="submitted" key="done" class="text-center">
            <div class="mx-success-icon">
              <v-icon size="40" color="primary">
                {{ alreadyRegistered ? 'mdi-account-check-outline' : 'mdi-check' }}
              </v-icon>
            </div>
            <h2 class="mx-title">
              {{ alreadyRegistered ? "You're already on the list" : "You're on the list!" }}
            </h2>
            <p class="mx-sub">
              <template v-if="alreadyRegistered">
                <strong>{{ submittedEmail }}</strong> is already saved, so there's nothing
                else you need to do. We'll email you there as soon as DriverX is back.
              </template>
              <template v-else>
                Thanks{{ form.firstName ? `, ${form.firstName}` : '' }}. We've sent a
                confirmation to <strong>{{ submittedEmail }}</strong> and will email you
                as soon as DriverX is back, so you can finish setting up your account.
              </template>
            </p>
            <p v-if="!alreadyRegistered" class="mx-hint">
              Can't find the email? Check your spam or promotions folder.
            </p>
            <v-btn text small color="primary" class="mt-4 mx-link-btn" @click="resetForm">
              Use a different email
            </v-btn>
          </div>

          <!-- Form -->
          <div v-else key="form">
            <div class="text-center">
              <span class="mx-badge">
                <span class="mx-dot" />
                Scheduled maintenance
              </span>
              <h2 class="mx-title">{{ headline }}</h2>
              <p class="mx-sub">
                We're upgrading DriverX to serve you better.
                Leave your details and we'll email you as soon as
                {{ isLoginVisit ? 'sign in' : 'sign-up' }} is back.
              </p>
            </div>

            <p v-if="isLoginVisit" class="mx-note">
              <v-icon small color="primary" class="mr-1">mdi-shield-check-outline</v-icon>
              Already have an account? Your account, bookings and payments are safe.
            </p>

            <p v-if="selectedPackage" class="mx-note">
              <v-icon small color="primary" class="mr-1">mdi-package-variant-closed</v-icon>
              We've saved the package you picked and will keep it for you.
            </p>

            <v-form ref="form" lazy-validation class="mt-6" @submit.prevent="submit">
              <p class="mx-label">Continue as a</p>
              <div class="btn-row" role="radiogroup" aria-label="Continue as a">
                <v-btn
                  v-for="r in roles"
                  :key="r.value"
                  small
                  role="radio"
                  :aria-checked="form.role === r.value ? 'true' : 'false'"
                  :class="{ active: form.role === r.value }"
                  @click="selectRole(r.value)"
                >
                  <div :class="['icon-box', r.color]">
                    <i :class="r.icon"></i>
                  </div>
                  {{ r.label }}
                  <v-spacer />
                  <v-icon v-if="form.role === r.value" color="primary" class="mr-3">
                    mdi-check-circle
                  </v-icon>
                </v-btn>
              </div>
              <div v-if="roleError" class="mx-error">Please choose one option</div>

              <div class="mx-row mt-2">
                <v-text-field
                  v-model.trim="form.firstName"
                  label="First name *"
                  outlined
                  dense
                  color="primary"
                  autocomplete="given-name"
                  :rules="[rules.required('First name')]"
                  hide-details="auto"
                />
                <v-text-field
                  v-model.trim="form.lastName"
                  label="Last name *"
                  outlined
                  dense
                  color="primary"
                  autocomplete="family-name"
                  :rules="[rules.required('Last name')]"
                  hide-details="auto"
                />
              </div>

              <v-text-field
                v-model.trim="form.email"
                label="Email *"
                type="email"
                inputmode="email"
                outlined
                dense
                color="primary"
                autocomplete="email"
                :rules="[rules.required('Email'), rules.email]"
                hide-details="auto"
                class="mx-field"
              />

              <div class="mx-row">
                <v-text-field
                  v-model.trim="form.phone"
                  label="Phone (optional)"
                  type="tel"
                  inputmode="tel"
                  outlined
                  dense
                  color="primary"
                  autocomplete="tel"
                  :rules="[rules.phone]"
                  hide-details="auto"
                  class="mx-field"
                />
                <v-text-field
                  v-model.trim="form.zip"
                  label="Postal code (optional)"
                  outlined
                  dense
                  color="primary"
                  autocomplete="postal-code"
                  hide-details="auto"
                  class="mx-field"
                />
              </div>

              <div class="mx-row">
                <v-text-field
                  v-model.trim="form.city"
                  label="City *"
                  outlined
                  dense
                  color="primary"
                  autocomplete="address-level2"
                  :rules="[rules.required('City')]"
                  hide-details="auto"
                  class="mx-field"
                />
                <v-select
                  v-model="form.licence"
                  :items="licenceOptions"
                  label="Current licence *"
                  outlined
                  dense
                  color="primary"
                  item-color="primary"
                  :menu-props="{ offsetY: true }"
                  :rules="[rules.required('Current licence')]"
                  hide-details="auto"
                  class="mx-field"
                />
              </div>

              <!-- Honeypot: hidden from people, bots tend to fill it -->
              <input
                v-model="honeypot"
                type="text"
                name="website"
                tabindex="-1"
                autocomplete="off"
                aria-hidden="true"
                class="mx-hp"
              />

              <v-alert
                v-if="errorMessage"
                dense
                text
                type="error"
                class="mt-5 mb-0 mx-alert"
              >
                {{ errorMessage }}
              </v-alert>

              <v-btn
                type="submit"
                block
                class="custom-btn mx-submit"
                :loading="loading"
                :disabled="loading"
              >
                Save my spot
              </v-btn>

              <p class="mx-hint text-center mt-4 mb-0">
                We'll only use these details to contact you about your DriverX account.
              </p>
            </v-form>
          </div>
        </transition>
      </v-card-text>
    </v-card>
  </AuthLayout>
</template>

<script>
import AuthLayout from '@/components/auth/AuthLayout'
import { maintenanceFormUrl } from '@/utils/maintenance'

const STORAGE_KEY = 'driverx_waitlist_email'
const REQUEST_TIMEOUT_MS = 20000

const ROLE_ALIASES = {
  student: 'student',
  learner: 'student',
  instructor: 'instructor',
  teacher: 'instructor'
}

// Ontario graduated licensing levels
const LICENCE_OPTIONS = [
  'No licence yet',
  'G1',
  'G2',
  'G (full licence)',
  'Out-of-province / International'
]

const emptyForm = () => ({
  role: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  zip: '',
  city: '',
  licence: ''
})

const firstQueryValue = (v) => (Array.isArray(v) ? v[0] : v) || ''

const safeStorage = {
  get (key) {
    try { return window.localStorage.getItem(key) } catch (e) { return null }
  },
  set (key, value) {
    try { window.localStorage.setItem(key, value) } catch (e) { /* storage unavailable */ }
  },
  remove (key) {
    try { window.localStorage.removeItem(key) } catch (e) { /* storage unavailable */ }
  }
}

export default {
  name: 'Maintenance',
  components: {
    AuthLayout
  },
  data () {
    return {
      loading: false,
      submitted: false,
      alreadyRegistered: false,
      submittedEmail: '',
      errorMessage: '',
      roleError: false,
      honeypot: '',
      // Same tiles/icons as the Signup screen
      roles: [
        { value: 'instructor', label: 'an instructor', icon: 'icon-education-cap', color: 'bg-green' },
        { value: 'student', label: 'a student', icon: 'icon-book', color: 'bg-yellow' }
      ],
      licenceOptions: LICENCE_OPTIONS,
      form: emptyForm(),
      rules: {
        required: (name) => (v) => !!(v && String(v).trim()) || `${name} is required`,
        email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v || '') || 'Enter a valid email',
        phone: (v) => !v || String(v).replace(/\D/g, '').length >= 7 || 'Enter a valid phone number'
      }
    }
  },
  computed: {
    query () {
      return this.$route.query || {}
    },
    fromRoute () {
      return firstQueryValue(this.query.from)
    },
    isLoginVisit () {
      return ['Login', 'ForgotPassword', '/login', '/forgot-password'].includes(this.fromRoute)
    },
    selectedPackage () {
      return firstQueryValue(this.query.package)
    },
    headline () {
      return this.isLoginVisit ? 'Sign in is paused for a moment' : "We'll be right back"
    }
  },
  created () {
    this.prefillFromQuery()
    const savedEmail = safeStorage.get(STORAGE_KEY)
    if (savedEmail) {
      this.submitted = true
      this.alreadyRegistered = true
      this.submittedEmail = savedEmail
    }
  },
  methods: {
    prefillFromQuery () {
      const q = this.query
      const role = ROLE_ALIASES[firstQueryValue(q.type || q.role).toLowerCase()]
      if (role) this.form.role = role

      const fullName = firstQueryValue(q.name).trim()
      const [nameFirst, ...nameRest] = fullName ? fullName.split(/\s+/) : []
      this.form.firstName = firstQueryValue(q.firstName || q.first_name) || nameFirst || ''
      this.form.lastName = firstQueryValue(q.lastName || q.last_name) || nameRest.join(' ')
      this.form.email = firstQueryValue(q.email)
      this.form.phone = firstQueryValue(q.phone)
      this.form.zip = firstQueryValue(q.zip || q.zipcode || q.zipCode || q.postalCode)
      this.form.city = firstQueryValue(q.city)
      const licence = firstQueryValue(q.licence || q.license).toLowerCase()
      this.form.licence = LICENCE_OPTIONS.find((o) => o.toLowerCase().split(' ')[0] === licence) || ''
    },
    selectRole (value) {
      this.form.role = value
      this.roleError = false
    },
    buildPayload () {
      const q = this.query
      const utm = ['utm_source', 'utm_medium', 'utm_campaign']
        .map((k) => firstQueryValue(q[k]))
        .filter(Boolean)
        .join(' / ')
      return {
        name: `${this.form.firstName} ${this.form.lastName}`.trim(),
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email.toLowerCase(),
        phone: this.form.phone,
        zip: this.form.zip,
        city: this.form.city,
        licence: this.form.licence,
        role: this.form.role,
        package: this.selectedPackage,
        priceId: firstQueryValue(q.priceId),
        from: this.fromRoute,
        source: utm || (document.referrer || '').slice(0, 200),
        platform: typeof window.cordova !== 'undefined' ? 'app' : 'web'
      }
    },
    async submit () {
      this.errorMessage = ''
      this.roleError = !this.form.role
      const fieldsValid = this.$refs.form.validate()
      if (!fieldsValid || this.roleError) return

      // Bot filled the hidden field: pretend it worked, send nothing.
      if (this.honeypot) {
        this.markSubmitted(this.form.email, false)
        return
      }

      if (!maintenanceFormUrl) {
        console.error('Maintenance_Form_URL is not set in .env')
        this.errorMessage = "We couldn't save your details right now. Please try again in a few minutes."
        return
      }

      this.loading = true
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timer = controller && setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
      try {
        const res = await fetch(maintenanceFormUrl, {
          method: 'POST',
          // text/plain keeps this a "simple" request, so Apps Script doesn't hit a CORS preflight
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(this.buildPayload()),
          signal: controller ? controller.signal : undefined
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data || !data.ok) {
          throw new Error((data && data.error) || `Request failed (${res.status})`)
        }
        this.markSubmitted(this.form.email, !!data.duplicate)
      } catch (err) {
        console.error('Waitlist submit failed:', err)
        this.errorMessage = err && err.message === 'invalid'
          ? 'Please check your name and email and try again.'
          : "We couldn't save your details. Please check your connection and try again."
      } finally {
        if (timer) clearTimeout(timer)
        this.loading = false
      }
    },
    markSubmitted (email, duplicate) {
      this.submittedEmail = email.toLowerCase()
      this.alreadyRegistered = duplicate
      this.submitted = true
      safeStorage.set(STORAGE_KEY, this.submittedEmail)
    },
    resetForm () {
      safeStorage.remove(STORAGE_KEY)
      this.submitted = false
      this.alreadyRegistered = false
      this.submittedEmail = ''
      this.errorMessage = ''
      this.honeypot = ''
      this.form = emptyForm()
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    }
  }
}
</script>

<style scoped>
.mx-wrap {
  padding: 32px 4px 40px;
}
.mx-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  border-radius: 999px;
  background: rgba(232, 163, 29, 0.12);
  color: #b57a0c;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.mx-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e8a31d;
  animation: mx-pulse 1.8s ease-in-out infinite;
}
@keyframes mx-pulse {
  50% { opacity: 0.35; }
}
.mx-title {
  margin: 16px 0 10px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: #000;
}
.mx-sub {
  margin: 0 auto;
  max-width: 440px;
  font-size: 14px;
  line-height: 1.7;
  color: #706f6f !important;
}
.mx-note {
  display: flex;
  align-items: flex-start;
  margin: 18px 0 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(45, 154, 71, 0.08);
  color: #2b5537 !important;
  font-size: 13px;
  line-height: 1.55;
}
.mx-label {
  margin: 0 0 8px;
  font-size: 14px;
  color: #706f6f !important;
}
.mx-error {
  margin: -6px 0 6px;
  font-size: 12px;
  color: #ff5252;
}
.mx-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0 12px;
}
.mx-row > * {
  margin-top: 14px;
}
.mx-field {
  margin-top: 14px;
}
.mx-submit {
  width: 100%;
  margin-top: 24px;
  height: 48px !important;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.mx-submit.v-btn--disabled {
  background: #2d9a47 !important;
  opacity: 0.7;
}
.mx-alert {
  border-radius: 10px;
  font-size: 13px;
}
.mx-hint {
  font-size: 12px;
  color: #9aa3a9 !important;
}
.mx-link-btn {
  text-transform: none;
  letter-spacing: 0;
}
.mx-hp {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.mx-success-icon {
  width: 80px;
  height: 80px;
  margin: 8px auto 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(45, 154, 71, 0.1);
}
.mx-fade-enter-active,
.mx-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.mx-fade-enter,
.mx-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Line the select's value up with the text fields (global input styles add left padding) */
.mx-field >>> .v-select__selections {
  padding-left: 15px !important;
}

/* Match Signup's role tile text style */
.btn-row >>> .v-btn {
  letter-spacing: 1.2px;
}

@media (max-width: 599px) {
  .mx-wrap {
    padding: 20px 2px 28px;
  }
  .mx-title {
    font-size: 21px;
  }
  .mx-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
